import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, Mail, MapPin, Clock, Send, Calendar, Users } from 'lucide-react';
import { createBooking, submitContactForm, tours, services } from '../mock/mockData';
import { toast } from 'sonner';

const ContactLuxury = () => {
  const navigate = useNavigate();
  const [bookingForm, setBookingForm] = useState({
    firstName: '',
    surname: '',
    idOrPassport: '',
    email: '',
    phone: '',
    tourId: '',
    date: '',
    guests: '1',
    numAdults: '1',
    numKids: '0',
    message: '',
    travelerType: 'south-african',
    saIdNumber: '',
    termsAccepted: false
  });

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('booking');
  const [selectedTourDetails, setSelectedTourDetails] = useState(null);

  useEffect(() => {
    const selectedTour = sessionStorage.getItem('selectedTour');
    if (selectedTour) {
      setBookingForm(prev => ({ ...prev, tourId: selectedTour }));
      sessionStorage.removeItem('selectedTour');
    }
  }, []);

  useEffect(() => {
    if (bookingForm.tourId) {
      const tour = [...tours, ...services].find(t => t.id === bookingForm.tourId);
      setSelectedTourDetails(tour);
    } else {
      setSelectedTourDetails(null);
    }
  }, [bookingForm.tourId]);

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const bookingResponse = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingForm),
      });

      if (!bookingResponse.ok) {
        throw new Error('Failed to create booking');
      }

      const bookingData = await bookingResponse.json();
      
      toast.success('Booking created successfully!');
      
      // Redirect to EFT payment instructions instead of Ikhokha
      navigate('/payment-instructions', {
        state: { booking: bookingData }
      });
      
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.message || 'Failed to process booking. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await submitContactForm(contactForm);
      toast.success('Thank you! We\'ll get back to you within 24 hours.');
      
      setContactForm({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Call Us',
      detail: '073 654 0090',
      href: 'https://wa.me/27736540090'
    },
    {
      icon: Mail,
      title: 'Email',
      detail: 'info@capeatiantictours.co.za',
      href: 'mailto:info@capeatiantictours.co.za'
    },
    {
      icon: MapPin,
      title: 'Location',
      detail: 'Cape Town, South Africa',
      href: null
    },
    {
      icon: Clock,
      title: 'Hours',
      detail: 'Mon - Sun: 8:00 AM - 8:00 PM',
      href: null
    }
  ];

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-white via-[#FAF8F5] to-[#FFF9F0]">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full text-[#C5A059] font-medium text-sm tracking-wide">
              GET IN TOUCH
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-[#0B090A] mb-6 tracking-tight">
            Start Your
            <span className="block text-[#6B0B0B]">Extraordinary Journey</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Ready to experience the magic of Cape Town? Book your adventure or reach out to us for a bespoke experience tailored to your dreams
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {/* Contact Info Cards */}
          {contactInfo.map((info, index) => (
            <div
              key={index}
              className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-[#D4AF37]/20"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#6B0B0B] to-[#8B1515] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <info.icon className="text-white" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">{info.title}</h3>
                  {info.href ? (
                    <a
                      href={info.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base font-semibold text-[#0B090A] hover:text-[#6B0B0B] transition-colors"
                    >
                      {info.detail}
                    </a>
                  ) : (
                    <p className="text-base font-semibold text-[#0B090A]">{info.detail}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Booking & Contact Form */}
        <div className="max-w-4xl mx-auto">
          <Card className="cinema-shadow border-2 border-[#D4AF37]/20 rounded-3xl overflow-hidden bg-white">
            {/* Tab Navigation */}
            <div className="bg-gradient-to-r from-[#FAF8F5] to-white border-b border-[#D4AF37]/20">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('booking')}
                  className={`flex-1 px-6 py-5 text-base font-semibold transition-all duration-300 ${
                    activeTab === 'booking'
                      ? 'bg-white text-[#6B0B0B] border-b-4 border-[#6B0B0B]'
                      : 'text-gray-500 hover:text-[#6B0B0B] hover:bg-white/50'
                  }`}
                >
                  <Calendar className="inline-block mr-2" size={20} />
                  Book Experience
                </button>
                <button
                  onClick={() => setActiveTab('contact')}
                  className={`flex-1 px-6 py-5 text-base font-semibold transition-all duration-300 ${
                    activeTab === 'contact'
                      ? 'bg-white text-[#6B0B0B] border-b-4 border-[#6B0B0B]'
                      : 'text-gray-500 hover:text-[#6B0B0B] hover:bg-white/50'
                  }`}
                >
                  <Send className="inline-block mr-2" size={20} />
                  General Inquiry
                </button>
              </div>
            </div>

            <CardContent className="p-8 md:p-10">
              {activeTab === 'booking' ? (
                <form onSubmit={handleBookingSubmit} className="space-y-6">
                  {/* Personal Info Section */}
                  <div className="pb-6 border-b border-gray-200">
                    <h3 className="text-xl font-semibold text-[#0B090A] mb-4 flex items-center">
                      <Users className="mr-2 text-[#C5A059]" size={22} />
                      Your Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="booking-firstName" className="text-gray-700 font-medium">First Name *</Label>
                        <Input
                          id="booking-firstName"
                          type="text"
                          required
                          value={bookingForm.firstName}
                          onChange={(e) => setBookingForm({ ...bookingForm, firstName: e.target.value })}
                          placeholder="John"
                          className="mt-2 border-gray-300 focus:border-[#6B0B0B] focus:ring-[#6B0B0B] rounded-lg"
                        />
                      </div>

                      <div>
                        <Label htmlFor="booking-surname" className="text-gray-700 font-medium">Surname *</Label>
                        <Input
                          id="booking-surname"
                          type="text"
                          required
                          value={bookingForm.surname}
                          onChange={(e) => setBookingForm({ ...bookingForm, surname: e.target.value })}
                          placeholder="Doe"
                          className="mt-2 border-gray-300 focus:border-[#6B0B0B] focus:ring-[#6B0B0B] rounded-lg"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <Label htmlFor="booking-idOrPassport" className="text-gray-700 font-medium">ID Number or Passport Number *</Label>
                        <Input
                          id="booking-idOrPassport"
                          type="text"
                          required
                          value={bookingForm.idOrPassport}
                          onChange={(e) => setBookingForm({ ...bookingForm, idOrPassport: e.target.value })}
                          placeholder="Enter your ID or Passport number"
                          className="mt-2 border-gray-300 focus:border-[#6B0B0B] focus:ring-[#6B0B0B] rounded-lg"
                        />
                        <p className="text-xs text-gray-500 mt-2">Required for booking verification and tour confirmation</p>
                      </div>

                      <div>
                        <Label htmlFor="booking-email" className="text-gray-700 font-medium">Email *</Label>
                        <Input
                          id="booking-email"
                          type="email"
                          required
                          value={bookingForm.email}
                          onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                          placeholder="john@example.com"
                          className="mt-2 border-gray-300 focus:border-[#6B0B0B] focus:ring-[#6B0B0B] rounded-lg"
                        />
                      </div>

                      <div>
                        <Label htmlFor="booking-phone" className="text-gray-700 font-medium">Phone *</Label>
                        <Input
                          id="booking-phone"
                          type="tel"
                          required
                          value={bookingForm.phone}
                          onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                          placeholder="+27 12 345 6789"
                          className="mt-2 border-gray-300 focus:border-[#6B0B0B] focus:ring-[#6B0B0B] rounded-lg"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Experience Selection */}
                  <div className="pb-6 border-b border-gray-200">
                    <h3 className="text-xl font-semibold text-[#0B090A] mb-4 flex items-center">
                      <Calendar className="mr-2 text-[#C5A059]" size={22} />
                      Experience Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="md:col-span-2">
                        <Label htmlFor="booking-tour" className="text-gray-700 font-medium">Select Experience *</Label>
                        <Select 
                          value={bookingForm.tourId} 
                          onValueChange={(value) => setBookingForm({ ...bookingForm, tourId: value })}
                        >
                          <SelectTrigger className="mt-2 border-gray-300 focus:border-[#6B0B0B] rounded-lg">
                            <SelectValue placeholder="Choose your adventure..." />
                          </SelectTrigger>
                          <SelectContent>
                            <div className="px-2 py-2 text-xs font-semibold text-[#C5A059] tracking-wide">TOURS & EXPERIENCES</div>
                            {tours.map(tour => (
                              <SelectItem key={tour.id} value={tour.id}>
                                {tour.title} - R{tour.price.toLocaleString()}
                              </SelectItem>
                            ))}
                            <div className="px-2 py-2 text-xs font-semibold text-[#C5A059] tracking-wide border-t mt-2">PREMIUM SERVICES</div>
                            {services.map(service => (
                              <SelectItem key={service.id} value={service.id}>
                                {service.title} - {service.price}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {selectedTourDetails?.hasDualPricing && (
                        <div className="md:col-span-2">
                          <Label className="text-gray-700 font-medium">Traveler Type *</Label>
                          <div className="grid grid-cols-2 gap-3 mt-2">
                            <button
                              type="button"
                              onClick={() => setBookingForm({ ...bookingForm, travelerType: 'south-african' })}
                              className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                                bookingForm.travelerType === 'south-african'
                                  ? 'border-[#6B0B0B] bg-[#6B0B0B]/5 shadow-lg'
                                  : 'border-gray-200 hover:border-[#D4AF37]/50'
                              }`}
                            >
                              <div className="font-semibold text-[#0B090A]">SA Citizen</div>
                              <div className="text-sm text-[#6B0B0B] font-bold mt-1">
                                R{selectedTourDetails.priceSACitizen?.toLocaleString()}
                              </div>
                            </button>
                            <button
                              type="button"
                              onClick={() => setBookingForm({ ...bookingForm, travelerType: 'international' })}
                              className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                                bookingForm.travelerType === 'international'
                                  ? 'border-[#6B0B0B] bg-[#6B0B0B]/5 shadow-lg'
                                  : 'border-gray-200 hover:border-[#D4AF37]/50'
                              }`}
                            >
                              <div className="font-semibold text-[#0B090A]">International</div>
                              <div className="text-sm text-[#6B0B0B] font-bold mt-1">
                                R{selectedTourDetails.priceInternational?.toLocaleString()}
                              </div>
                            </button>
                          </div>
                        </div>
                      )}

                      {selectedTourDetails?.requiresIdNumber && bookingForm.travelerType === 'south-african' && (
                        <div className="md:col-span-2">
                          <Label htmlFor="sa-id-number" className="text-gray-700 font-medium">South African ID Number *</Label>
                          <Input
                            id="sa-id-number"
                            type="text"
                            required
                            value={bookingForm.saIdNumber}
                            onChange={(e) => setBookingForm({ ...bookingForm, saIdNumber: e.target.value })}
                            placeholder="Enter your SA ID number"
                            className="mt-2 border-gray-300 focus:border-[#6B0B0B] rounded-lg"
                          />
                          <p className="text-xs text-gray-500 mt-2">Required by Robben Island Museum for booking confirmation</p>
                        </div>
                      )}

                      <div>
                        <Label htmlFor="booking-date" className="text-gray-700 font-medium">Preferred Date *</Label>
                        <Input
                          id="booking-date"
                          type="date"
                          required
                          value={bookingForm.date}
                          onChange={(e) => setBookingForm({ ...bookingForm, date: e.target.value })}
                          min={new Date().toISOString().split('T')[0]}
                          className="mt-2 border-gray-300 focus:border-[#6B0B0B] rounded-lg"
                        />
                      </div>

                      {selectedTourDetails?.pricingType === 'age-based' ? (
                        <>
                          <div>
                            <Label htmlFor="num-adults" className="text-gray-700 font-medium">Number of Adults *</Label>
                            <Select 
                              value={bookingForm.numAdults} 
                              onValueChange={(value) => setBookingForm({ ...bookingForm, numAdults: value })}
                            >
                              <SelectTrigger className="mt-2 border-gray-300 rounded-lg">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                  <SelectItem key={num} value={String(num)}>
                                    {num} {num === 1 ? 'Adult' : 'Adults'} - R{(selectedTourDetails.price * num).toLocaleString()}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500 mt-2">Adult: R{selectedTourDetails.price.toLocaleString()}/person</p>
                          </div>

                          <div>
                            <Label htmlFor="num-kids" className="text-gray-700 font-medium">Number of Kids (Under 17) *</Label>
                            <Select 
                              value={bookingForm.numKids} 
                              onValueChange={(value) => setBookingForm({ ...bookingForm, numKids: value })}
                            >
                              <SelectTrigger className="mt-2 border-gray-300 rounded-lg">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                  <SelectItem key={num} value={String(num)}>
                                    {num} {num === 1 ? 'Kid' : 'Kids'} - R{(selectedTourDetails.priceKids * num).toLocaleString()}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500 mt-2">Kids: R{selectedTourDetails.priceKids.toLocaleString()}/person</p>
                          </div>

                          {(parseInt(bookingForm.numAdults) > 0 || parseInt(bookingForm.numKids) > 0) && (
                            <div className="md:col-span-2 p-5 bg-gradient-to-r from-[#D4AF37]/10 to-[#C5A059]/10 border-l-4 border-[#D4AF37] rounded-xl">
                              <div className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                                <span className="text-xl mr-2">💎</span>
                                Total Investment
                              </div>
                              {parseInt(bookingForm.numAdults) > 0 && (
                                <div className="text-sm text-gray-700 mb-2">
                                  {bookingForm.numAdults} Adult{parseInt(bookingForm.numAdults) !== 1 ? 's' : ''} × R{selectedTourDetails.price.toLocaleString()} = <span className="font-semibold">R{(parseInt(bookingForm.numAdults) * selectedTourDetails.price).toLocaleString()}</span>
                                </div>
                              )}
                              {parseInt(bookingForm.numKids) > 0 && (
                                <div className="text-sm text-gray-700 mb-3">
                                  {bookingForm.numKids} Kid{parseInt(bookingForm.numKids) !== 1 ? 's' : ''} × R{selectedTourDetails.priceKids.toLocaleString()} = <span className="font-semibold">R{(parseInt(bookingForm.numKids) * selectedTourDetails.priceKids).toLocaleString()}</span>
                                </div>
                              )}
                              <div className="text-lg font-bold text-[#6B0B0B] pt-3 border-t border-[#D4AF37]/30">
                                Total: R{((parseInt(bookingForm.numAdults) * selectedTourDetails.price) + (parseInt(bookingForm.numKids) * selectedTourDetails.priceKids)).toLocaleString()}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div>
                          <Label htmlFor="booking-guests" className="text-gray-700 font-medium">Number of Guests *</Label>
                          <Select 
                            value={bookingForm.guests} 
                            onValueChange={(value) => setBookingForm({ ...bookingForm, guests: value })}
                          >
                            <SelectTrigger className="mt-2 border-gray-300 rounded-lg">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                <SelectItem key={num} value={String(num)}>
                                  {num} {num === 1 ? 'Guest' : 'Guests'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div>
                    <Label htmlFor="booking-message" className="text-gray-700 font-medium">Special Requests</Label>
                    <Textarea
                      id="booking-message"
                      value={bookingForm.message}
                      onChange={(e) => setBookingForm({ ...bookingForm, message: e.target.value })}
                      placeholder="Any dietary requirements, accessibility needs, or special requests?"
                      rows={4}
                      className="mt-2 border-gray-300 focus:border-[#6B0B0B] rounded-lg"
                    />
                  </div>

                  {/* Terms and Conditions Acceptance */}
                  <div className="pt-6 border-t border-gray-200">
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="termsAccepted"
                        required
                        checked={bookingForm.termsAccepted}
                        onChange={(e) => setBookingForm({ ...bookingForm, termsAccepted: e.target.checked })}
                        className="mt-1 w-4 h-4 text-[#6B0B0B] border-gray-300 rounded focus:ring-[#6B0B0B]"
                      />
                      <label htmlFor="termsAccepted" className="text-sm text-gray-700 leading-relaxed">
                        I have read and agree to the{' '}
                        <a href="/terms" target="_blank" className="text-[#6B0B0B] underline hover:text-[#8B1515]">
                          Terms of Service
                        </a>
                        ,{' '}
                        <a href="/privacy" target="_blank" className="text-[#6B0B0B] underline hover:text-[#8B1515]">
                          Privacy Policy
                        </a>
                        , and{' '}
                        <a href="/cancellation" target="_blank" className="text-[#6B0B0B] underline hover:text-[#8B1515]">
                          Cancellation Policy
                        </a>
                        . I understand that by proceeding with this booking, I am entering into a legally binding agreement. *
                      </label>
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button
                      type="submit"
                      disabled={isSubmitting || !bookingForm.termsAccepted}
                      className="w-full bg-gradient-to-r from-[#6B0B0B] to-[#8B1515] hover:from-[#8B1515] hover:to-[#6B0B0B] text-white py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing Your Booking...
                        </span>
                      ) : (
                        'Continue to Payment Instructions'
                      )}
                    </Button>
                    <p className="text-xs text-center text-gray-500 mt-4">
                      🏦 Secure EFT/Bank Transfer • Complete your booking with simple bank payment
                    </p>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="contact-name" className="text-gray-700 font-medium">Full Name *</Label>
                      <Input
                        id="contact-name"
                        type="text"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        placeholder="John Doe"
                        className="mt-2 border-gray-300 focus:border-[#6B0B0B] rounded-lg"
                      />
                    </div>

                    <div>
                      <Label htmlFor="contact-email" className="text-gray-700 font-medium">Email *</Label>
                      <Input
                        id="contact-email"
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        placeholder="john@example.com"
                        className="mt-2 border-gray-300 focus:border-[#6B0B0B] rounded-lg"
                      />
                    </div>

                    <div>
                      <Label htmlFor="contact-phone" className="text-gray-700 font-medium">Phone</Label>
                      <Input
                        id="contact-phone"
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        placeholder="+27 12 345 6789"
                        className="mt-2 border-gray-300 focus:border-[#6B0B0B] rounded-lg"
                      />
                    </div>

                    <div>
                      <Label htmlFor="contact-subject" className="text-gray-700 font-medium">Subject *</Label>
                      <Input
                        id="contact-subject"
                        type="text"
                        required
                        value={contactForm.subject}
                        onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                        placeholder="How can we help?"
                        className="mt-2 border-gray-300 focus:border-[#6B0B0B] rounded-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="contact-message" className="text-gray-700 font-medium">Message *</Label>
                    <Textarea
                      id="contact-message"
                      required
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder="Tell us about your dream Cape Town experience..."
                      rows={6}
                      className="mt-2 border-gray-300 focus:border-[#6B0B0B] rounded-lg"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#6B0B0B] to-[#8B1515] hover:from-[#8B1515] hover:to-[#6B0B0B] text-white py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]"
                  >
                    {isSubmitting ? 'Sending...' : (
                      <>
                        <Send className="inline-block mr-2" size={20} />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactLuxury;
